import React, { useEffect, useState } from 'react'
import AddEditForm from '../../../sharedComponents/AddEditForm'
import { PencilIcon, TrashIcon } from '@heroicons/react/24/solid'
import Modal from 'react-modal';
import DeleteConfirm from '../../../sharedComponents/DeleteConfirm';
import { toast } from 'react-toastify';
import useManageAdmins from './useManageAdmins';
import AdminService from './AdminService';
import WhtCertificateService from '../../company/whtCertificate/WhtCertificateService';

const customStyles = {
    content: {
      top: '50%',
      left: '50%',
      right: 'auto',
      bottom: 'auto',
      height: 'auto',
      width: '600px',
      maxHeight: '90vh',
      overflowY: 'auto',
      marginRight: '-50%',
      transform: 'translate(-50%, -50%)',
    },
  };

// Social security is 5% of salary PLUS position allowance, capped by the 17,500
// wage ceiling (= ฿875 a month), in whole baht with standard rounding. Derived,
// never typed: a figure entered by hand silently changes what is deducted from
// someone's pay every month, and nothing downstream would flag it.
// Shown here for the operator; the server recomputes it on save and that value
// is what is stored — mirrors socialSecurityFor() in payrollFormulas.js.
const SOCIAL_SECURITY_RATE = 0.05;
const SOCIAL_SECURITY_CAP = 875;
const socialSecurityFor = (salary, positionAllowance) =>
  Math.min(
    Math.round(((Number(salary) || 0) + (Number(positionAllowance) || 0)) * SOCIAL_SECURITY_RATE),
    SOCIAL_SECURITY_CAP
  );

// Weekly roster for the chat-feedback deadline, typed one shift per line:
//   Mon 07:00-17:00
//   Sat 19:00-02:00      (end before start = ends the next day)
// Stored as [{ weekday (0 = Sun), startMin, endMin }], minutes from midnight.
// One line per day: an admin covering both pages types the whole span once.
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const SHIFT_LINE = /^(sun|mon|tue|wed|thu|fri|sat)\s+(\d{1,2}):(\d{2})\s*-\s*(\d{1,2}):(\d{2})$/i;

const parseShiftSlotsText = (text) => {
  const slots = [];
  const lines = (text || '').split('\n').map((l) => l.trim()).filter(Boolean);
  for (const line of lines) {
    const m = SHIFT_LINE.exec(line);
    if (!m) throw new Error(`Shift line not understood: "${line}" (use e.g. Mon 07:00-17:00)`);
    const startMin = Number(m[2]) * 60 + Number(m[3]);
    let endMin = Number(m[4]) * 60 + Number(m[5]);
    if (endMin <= startMin) endMin += 24 * 60;
    slots.push({
      weekday: DAY_NAMES.findIndex((d) => d.toLowerCase() === m[1].toLowerCase()),
      startMin,
      endMin,
    });
  }
  return slots;
};

const hhmm = (min) => `${String(Math.floor((min % 1440) / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`;
const formatShiftSlots = (slots) => (Array.isArray(slots) ? slots : [])
  .map((s) => `${DAY_NAMES[s.weekday]} ${hhmm(s.startMin)}-${hhmm(s.endMin)}`)
  .join('\n');

const AdminSetting = () => {
  const [mode, setMode] = useState('register')
  const [modalTitle, setModalTitle] = useState('Register Admin Information')
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [admin, setAdmin] = useState({
    id: '',
    nickname: '',
    userNickname: '',
    isOwner: false,
    isHelper: false,
    salary: '',
    employmentType: 'fulltime',
    whtPayeeId: '',
    isCompanyEmployee: false,
    hasFifthRun: true,
    socialSecurityAmount: '',
    studentLoan: '',
    fixedWhtPerMonth: '',
    paymentFrequency: 'twice',
    startWorkingDate: '',
    workingHours: '',
    noMistakeIncentiveAmount: '',
    positionAllowance: '',
    startingHolidayDays: '',
    bankName: '',
    bankAccountNo: '',
    bankAccountName: '',
    // These 2 fields are the UI inputs; we compose shopWorkingHours JSON on submit.
    smileWorkingHours: '',
    tamaWorkingHours: '',
    // Chat-feedback: the name the Chatcone audit report uses for this admin, and
    // their weekly roster as text (parsed into shiftSlots on submit).
    chatconeName: '',
    shiftSlotsText: '',
    isActive: true
  });

  // Payee options for the part-time WHT link. Failing to load must not block the
  // admin form, so an error just leaves the list empty.
  const [whtPayees, setWhtPayees] = useState([]);
  useEffect(() => {
    WhtCertificateService.listPayees()
      .then(setWhtPayees)
      .catch(() => setWhtPayees([]));
  }, []);

  const {
    admins,
    availableUsers,
    isLoading,
    error,
    addAdmin,
    editAdmin,
    deleteAdmin,
    refreshAdmins,
    refreshAvailableUsers
  } = useManageAdmins();

  // Create items array with dynamic user options
  const getFormItems = () => {
    // The login link is optional — an admin can exist without a user account.
    const userOptions = [
      { value: '', label: '— no link —' },
      ...availableUsers.map(user => ({
        value: user.nickname,
        label: user.nickname
      }))
    ];
    const ownerOrHelper = admin.isOwner || admin.isHelper;

    // availableUsers excludes anyone who already HAS an admin record — correct when
    // registering, wrong when editing: the admin being edited was filtered out, so
    // their name had no matching option and the field rendered blank.
    if (mode === 'edit' && admin.userNickname
        && !userOptions.some((o) => o.value === admin.userNickname)) {
      userOptions.unshift({ value: admin.userNickname, label: admin.userNickname });
    }

    // Ordered the way the form is actually filled in: who they are, what kind of
    // employment, what they are paid, how that pay is split into runs, their
    // hours, then the bank. Salary and position allowance sit together because
    // they are one pay base — social security derives from the two of them, so it
    // renders directly below both of its inputs rather than above them.
    return [
      {
        title: 'Nickname',
        name: 'nickname',
        type: 'text',
      },
      {
        title: 'Linked User (optional)',
        name: 'userNickname',
        type: 'select',
        hint: 'Only a linked admin can see their own KPI / mistakes / tasks when logged in.',
        options: userOptions
      },
      {
        title: 'Owner',
        name: 'isOwner',
        type: 'boolean',
      },
      {
        title: 'Helper',
        name: 'isHelper',
        type: 'boolean',
      },
      {
        // Part-time changes only the tax and the paperwork: 3% is withheld from
        // every run and a certificate is issued per payment. Payment frequency and
        // the 5th run work exactly the same for both types.
        title: 'Employment Type',
        name: 'employmentType',
        type: 'select',
        options: [
          { value: 'fulltime', label: 'Full-time' },
          { value: 'parttime', label: 'Part-time / Freelance (3% WHT)' }
        ]
      },
      // The payee record holds the tax id and address the WHT certificate needs.
      // Offered to company employees too, not just part-timers: a company employee
      // on a fixed monthly withholding gets an ANNUAL 50 ทวิ, and without a payee
      // linked there is no tax id or address to put on it, so issuing it refuses.
      ...(admin.employmentType === 'parttime' || admin.isCompanyEmployee
        ? [
            {
              title: 'WHT Payee (for the 50 ทวิ certificate)',
              name: 'whtPayeeId',
              type: 'select',
              hint: admin.employmentType === 'parttime'
                ? 'Needed for the certificate issued with every payment.'
                : 'Needed only if withholding tax is taken — one certificate is issued per year.',
              options: [
                { value: '', label: '— none yet —' },
                ...whtPayees.map(p => ({ value: String(p.id), label: p.taxId ? `${p.name} (${p.taxId})` : p.name }))
              ]
            },
          ]
        : []),
      {
        title: 'Start Working Date',
        name: 'startWorkingDate',
        type: 'date',
      },
      {
        // Tenure milestones add a day to this at 1 and 3 years from the date above.
        title: 'Starting Holiday Days',
        name: 'startingHolidayDays',
        type: 'number',
      },
      {
        title: 'Salary',
        name: 'salary',
        type: 'number',
      },
      // Owners and helpers get no position allowance — the backend stores 0.
      ...(ownerOrHelper
        ? []
        : [
            {
              // Paid as its own ½/½ component, but it also joins salary as the base the
              // OT rate, unpaid leave and social security are all computed from.
              title: 'Position Allowance',
              name: 'positionAllowance',
              type: 'number',
            },
          ]),
      {
        title: 'Company Employee',
        name: 'isCompanyEmployee',
        type: 'boolean',
      },
      // Social security and student loan only exist for company employees, so
      // they are not offered at all otherwise — and the backend clears them.
      ...(admin.isCompanyEmployee
        ? [
            {
              title: 'Social Security Amount',
              name: 'socialSecurityAmount',
              type: 'number',
              // An owner's figure is set by hand: a director (กรรมการ) cannot pay it.
              disabled: !admin.isOwner,
              hint: admin.isOwner
                ? 'Owner: set by hand. Use 0 for a director (กรรมการ) who cannot pay social security.'
                : 'Calculated: 5% of salary + position allowance, capped at ฿875 (the 17,500 wage ceiling).',
            },
            {
              title: 'Student Loan (กยศ)',
              name: 'studentLoan',
              type: 'number',
            },
            {
              // Salaried withholding is not a flat rate — the accountant works out
              // the year's progressive tax, and this is one month of it.
              title: 'Monthly Withholding Tax (฿)',
              name: 'fixedWhtPerMonth',
              type: 'number',
              step: '0.01',
              hint: 'Fixed baht withheld each month, split across the pay runs. Blank = none. Part-timers use the flat 3% instead.',
            },
          ]
        : []),
      {
        title: 'Payment Frequency',
        name: 'paymentFrequency',
        type: 'select',
        options: [
          { value: 'once', label: 'Once (End of Month)' },
          { value: 'twice', label: 'Twice (Mid & End)' }
        ]
      },
      {
        // Independent of payment frequency — a once-paid admin may still get a
        // 5th run, and a twice-paid one may not.
        title: 'Gets 5th Run (KPI / OT / bonus)',
        name: 'hasFifthRun',
        type: 'boolean',
      },
      {
        // Only ever paid through the 5th run, so it sits with it.
        title: 'No Mistake Incentive Amount',
        name: 'noMistakeIncentiveAmount',
        type: 'number',
      },
      {
        title: 'Working Hours',
        name: 'workingHours',
        type: 'number',
      },
      // Smile + Tama must add up to the total above — checked on submit, so the
      // three stay adjacent.
      {
        title: 'Smile Working Hours',
        name: 'smileWorkingHours',
        type: 'number',
        step: '0.25',
      },
      {
        title: 'Tama Working Hours',
        name: 'tamaWorkingHours',
        type: 'number',
        step: '0.25',
      },
      {
        title: 'Bank Name',
        name: 'bankName',
        type: 'text',
      },
      {
        title: 'Bank Account No',
        name: 'bankAccountNo',
        type: 'text',
      },
      {
        title: 'Bank Account Name',
        name: 'bankAccountName',
        type: 'text',
      },
      // Set after the admin exists: registerAdmin does not accept these.
      ...(mode === 'edit' ? [
        {
          title: 'Chatcone Name',
          name: 'chatconeName',
          type: 'text',
          hint: 'Exactly as it appears in Chatcone / the daily chat audit report. Links report findings to this admin.',
        },
        {
          title: 'Chat Shifts',
          name: 'shiftSlotsText',
          type: 'textarea',
          hint: 'One per line: Mon 07:00-17:00. End before start means overnight (Sat 19:00-02:00). Sets the chat-feedback deadline.',
        },
      ] : []),
    ];
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const updatedAdmin = { ...admin, [name]: type === 'checkbox' ? checked : value };

    // Taking someone off the company books clears their deductions here too, so
    // the form matches what the backend will store rather than hiding stale values.
    if (name === 'isCompanyEmployee' && !checked) {
      updatedAdmin.socialSecurityAmount = '';
      updatedAdmin.studentLoan = '';
      // The backend clears this off the books too, so the form must not keep
      // showing a figure that will no longer be withheld.
      updatedAdmin.fixedWhtPerMonth = '';
    }

    // Owners and helpers get no position allowance; the backend stores 0.
    if ((name === 'isOwner' || name === 'isHelper') && checked) {
      updatedAdmin.positionAllowance = '';
    }

    // Salary and position allowance both drive social security, so it follows
    // them instead of going stale at whatever it was when the row was last saved.
    // Not for an owner, whose figure is typed by hand.
    if ((name === 'salary' || name === 'positionAllowance' || name === 'isCompanyEmployee'
         || name === 'isOwner' || name === 'isHelper')
        && updatedAdmin.isCompanyEmployee && !updatedAdmin.isOwner) {
      updatedAdmin.socialSecurityAmount = socialSecurityFor(
        updatedAdmin.salary,
        updatedAdmin.positionAllowance
      );
    }

    setAdmin(updatedAdmin);
  };

  const handleDateChange = (date, fieldName) => {
    const formattedDate = date ? date.toISOString().split('T')[0] : '';
    setAdmin({ ...admin, [fieldName]: formattedDate });
  };

  const buildShopWorkingHours = (adminLike) => {
    const smile = adminLike.smileWorkingHours === '' || adminLike.smileWorkingHours === null || adminLike.smileWorkingHours === undefined
      ? undefined
      : Number(adminLike.smileWorkingHours);
    const tama = adminLike.tamaWorkingHours === '' || adminLike.tamaWorkingHours === null || adminLike.tamaWorkingHours === undefined
      ? undefined
      : Number(adminLike.tamaWorkingHours);

    const shopWorkingHours = {};
    if (smile !== undefined && !Number.isNaN(smile)) shopWorkingHours.smile = smile;
    if (tama !== undefined && !Number.isNaN(tama)) shopWorkingHours.tama = tama;

    return Object.keys(shopWorkingHours).length ? shopWorkingHours : null;
  };

  const validateShopHoursMatchTotal = (adminLike) => {
    const total = adminLike.workingHours === '' || adminLike.workingHours === null || adminLike.workingHours === undefined
      ? undefined
      : Number(adminLike.workingHours);

    const smile = adminLike.smileWorkingHours === '' || adminLike.smileWorkingHours === null || adminLike.smileWorkingHours === undefined
      ? 0
      : Number(adminLike.smileWorkingHours);
    const tama = adminLike.tamaWorkingHours === '' || adminLike.tamaWorkingHours === null || adminLike.tamaWorkingHours === undefined
      ? 0
      : Number(adminLike.tamaWorkingHours);

    const hasAnyShop = (adminLike.smileWorkingHours !== '' && adminLike.smileWorkingHours !== null && adminLike.smileWorkingHours !== undefined)
      || (adminLike.tamaWorkingHours !== '' && adminLike.tamaWorkingHours !== null && adminLike.tamaWorkingHours !== undefined);

    if (!hasAnyShop) return true;

    if (total === undefined || Number.isNaN(total)) {
      toast.error('Please set "Working Hours" (total) to match shop working hours.');
      return false;
    }
    if (Number.isNaN(smile) || Number.isNaN(tama)) {
      toast.error('Shop working hours must be numbers.');
      return false;
    }

    const sum = smile + tama;
    // tolerate small decimal rounding issues (e.g. 0.1 + 0.2)
    if (Math.abs(sum - total) > 0.001) {
      toast.error(`Shop working hours mismatch: Smile(${smile}) + Tama(${tama}) = ${sum}, but Working Hours(total) = ${total}`);
      return false;
    }

    return true;
  };

  const openForm = async (isEdit, id) => {
    if (isEdit) {
      const adminToEdit = admins.find(a => a.id === id);
      if (adminToEdit) {
        const existing = adminToEdit.shopWorkingHours && typeof adminToEdit.shopWorkingHours === 'object'
          ? adminToEdit.shopWorkingHours
          : null;

        setAdmin({
          id: adminToEdit.id,
          nickname: adminToEdit.nickname || '',
          userNickname: adminToEdit.user?.nickname || '',
          isOwner: !!adminToEdit.isOwner,
          isHelper: !!adminToEdit.isHelper,
          salary: adminToEdit.salary || '',
          employmentType: adminToEdit.employmentType || 'fulltime',
          whtPayeeId: adminToEdit.whtPayeeId ? String(adminToEdit.whtPayeeId) : '',
          isCompanyEmployee: !!adminToEdit.isCompanyEmployee,
          hasFifthRun: adminToEdit.hasFifthRun !== false,
          // Derived on open too, so a legacy hand-entered value is corrected rather
          // than silently carried forward the next time this admin is saved.
          // An owner's is hand-set, so the stored figure is shown as-is.
          socialSecurityAmount: !adminToEdit.isCompanyEmployee
            ? ''
            : adminToEdit.isOwner
              ? (adminToEdit.socialSecurityAmount ?? '')
              : socialSecurityFor(adminToEdit.salary, adminToEdit.positionAllowance),
          studentLoan: adminToEdit.studentLoan || '',
          // ?? not || so a stored 0 survives as 0 rather than becoming blank.
          fixedWhtPerMonth: adminToEdit.fixedWhtPerMonth ?? '',
          paymentFrequency: adminToEdit.paymentFrequency || 'twice',
          startWorkingDate: adminToEdit.startWorkingDate || '',
          workingHours: adminToEdit.workingHours || '',
          noMistakeIncentiveAmount: adminToEdit.noMistakeIncentiveAmount || '',
          positionAllowance: adminToEdit.positionAllowance || '',
          startingHolidayDays: adminToEdit.startingHolidayDays || '',
          bankName: adminToEdit.bankName || '',
          bankAccountNo: adminToEdit.bankAccountNo || '',
          bankAccountName: adminToEdit.bankAccountName || '',
          smileWorkingHours: existing?.smile ?? '',
          tamaWorkingHours: existing?.tama ?? '',
          chatconeName: adminToEdit.chatconeName || '',
          shiftSlotsText: formatShiftSlots(adminToEdit.shiftSlots),
          isActive: adminToEdit.isActive !== false
        });
        setModalTitle('Update Admin Information');
        setMode('edit');
      }
    } else {
      setAdmin({
        id: '',
        nickname: '',
        userNickname: '',
        isOwner: false,
        isHelper: false,
        salary: '',
        employmentType: 'fulltime',
        whtPayeeId: '',
        isCompanyEmployee: false,
        hasFifthRun: true,
        socialSecurityAmount: '',
        studentLoan: '',
        fixedWhtPerMonth: '',
        paymentFrequency: 'twice',
        startWorkingDate: '',
        workingHours: '',
        noMistakeIncentiveAmount: '',
        positionAllowance: '',
        startingHolidayDays: '',
        bankName: '',
        bankAccountNo: '',
        bankAccountName: '',
        smileWorkingHours: '',
        tamaWorkingHours: ''
      });
      setMode('register');
      setModalTitle('Register Admin Information');
    }
    setIsFormOpen(true);
  };

  const closeForm = () => {
    setIsFormOpen(false);
  };

  const openConfirm = async (id) => {
    const adminToDelete = admins.find(a => a.id === id);
    if (adminToDelete) {
      setAdmin(adminToDelete);
      setIsConfirmOpen(true);
    }
  };

  const closeConfirm = () => {
    setIsConfirmOpen(false);
  };

  const handleRegisterAdmin = async () => {
    try {
      if (!validateShopHoursMatchTotal(admin)) return;
      const payload = { ...admin };
      payload.shopWorkingHours = buildShopWorkingHours(payload);
      delete payload.smileWorkingHours;
      delete payload.tamaWorkingHours;
      delete payload.chatconeName;
      delete payload.shiftSlotsText;

      const returnResult = await addAdmin(payload);
      if (returnResult) {
        toast.success('Admin registered successfully!');
        closeForm();
      } else {
        toast.error(error);
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || "An unexpected error occurred.";
      toast.error(errorMessage);
    }
  };

  useEffect(() => {
    toast.error(error);
  }, [error]);

  const handleUpdateAdmin = async () => {
    try {
      if (!validateShopHoursMatchTotal(admin)) return;
      const payload = { ...admin };
      payload.shopWorkingHours = buildShopWorkingHours(payload);
      delete payload.smileWorkingHours;
      delete payload.tamaWorkingHours;
      try {
        payload.shiftSlots = parseShiftSlotsText(payload.shiftSlotsText);
      } catch (parseError) {
        toast.error(parseError.message);
        return;
      }
      delete payload.shiftSlotsText;

      await editAdmin(admin.id, payload);
      toast.success('Admin updated successfully!');
      closeForm();
    } catch (error) {
      const errorMessage = error.response?.data?.message || "An unexpected error occurred.";
      toast.error(errorMessage);
    }
  };

  const handleDeleteAdmin = async (id) => {
    try {
      await deleteAdmin(id);
      toast.success('Admin deleted successfully!');
      closeConfirm();
    } catch (error) {
      const errorMessage = error.response?.data?.message || "An unexpected error occurred.";
      toast.error(errorMessage);
    }
  };

  const handleToggleActive = async (adminItem) => {
    const newValue = adminItem.isActive === false ? true : false;
    try {
      // Only the flag: resending the whole row would also resend a blank nickname
      // or a derived figure the server would then reject or recompute.
      await AdminService.editAdmin(adminItem.id, { isActive: newValue });
      toast.success(`Admin ${newValue ? 'activated' : 'deactivated'} successfully`);
      refreshAdmins();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update status');
    }
  };

  useEffect(() => {
    refreshAdmins();
    refreshAvailableUsers();
  }, []);

  // Inactive admins are hidden unless asked for, and always listed after the
  // active ones (sort is stable, so each group keeps its original order).
  const visibleAdmins = admins
    .filter(a => showInactive || a.isActive !== false)
    .sort((a, b) => (a.isActive === false) - (b.isActive === false));
  const inactiveCount = admins.filter(a => a.isActive === false).length;

  const formatCurrency = (value) => {
    if (!value && value !== 0) return '-';
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: 'THB',
      minimumFractionDigits: 2
    }).format(value);
  };

  return (
    <div>
      <Modal
        isOpen={isFormOpen}
        onRequestClose={closeForm}
        style={customStyles}
        ariaHideApp={false}
      >
        <AddEditForm 
          items={getFormItems()} 
          name={modalTitle} 
          data={admin} 
          mode={mode}
          closeForm={closeForm}
          handleInputChange={handleInputChange}
          handleDateChange={handleDateChange}
          handleRegister={handleRegisterAdmin}
          handleUpdate={handleUpdateAdmin}
          stateValue={admin}
        />
      </Modal>
      <Modal
        isOpen={isConfirmOpen}
        onRequestClose={closeConfirm}
        style={customStyles}
        ariaHideApp={false}
      >
        <DeleteConfirm 
          items={getFormItems()} 
          data={admin} 
          closeConfirm={closeConfirm}
          confirmFunction={() => handleDeleteAdmin(admin.id)}
        />
      </Modal>
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="mt-4 flex flex-col">
          <div className='flex justify-between items-center mb-4'>
            <h2 className='text-5xl'>Admin Setting</h2>
            <label className='ml-auto mr-4 flex items-center gap-2 text-sm text-gray-700 cursor-pointer'>
              <input
                type='checkbox'
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
              />
              Show inactive ({inactiveCount})
            </label>
            <button
              onClick={() => openForm(false, null)} 
              className='bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded'
            >
              Add New Admin
            </button>
          </div>
          <div className="shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg overflow-hidden">
            <div className="overflow-y-auto" style={{ maxHeight: 'calc(100vh - 200px)' }}>
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50 sticky top-0 z-0">
                  <tr>
                    <th scope="col" className="py-4 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                      No
                    </th>
                    <th scope="col" className="px-3 py-4 text-left text-sm font-semibold text-gray-900">
                      Name
                    </th>
                    <th scope="col" className="px-3 py-4 text-left text-sm font-semibold text-gray-900">
                      Chatcone
                    </th>
                    <th scope="col" className="px-3 py-4 text-left text-sm font-semibold text-gray-900">
                      Salary
                    </th>
                    <th scope="col" className="px-3 py-4 text-left text-sm font-semibold text-gray-900">
                      Working Hours
                    </th>
                    <th scope="col" className="px-3 py-4 text-left text-sm font-semibold text-gray-900">
                      Monthly WHT
                    </th>
                    <th scope="col" className="px-3 py-4 text-left text-sm font-semibold text-gray-900">
                      Student Loan
                    </th>
                    <th scope="col" className="px-3 py-4 text-left text-sm font-semibold text-gray-900">
                      Company
                    </th>
                    <th scope="col" className="px-3 py-4 text-left text-sm font-semibold text-gray-900">
                      Payment
                    </th>
                    <th scope="col" className="px-3 py-4 text-left text-sm font-semibold text-gray-900">
                      Bank Account
                    </th>
                    <th scope="col" className="px-3 py-4 text-left text-sm font-semibold text-gray-900">
                      Status
                    </th>
                    <th scope="col" className="relative py-4 pl-3 pr-4 sm:pr-6">
                      <span className="sr-only">Edit</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {visibleAdmins.map((adminItem, index) => {
                    const { id, nickname, user, isOwner, isHelper, salary, workingHours, fixedWhtPerMonth, studentLoan, isCompanyEmployee, employmentType, paymentFrequency, hasFifthRun, bankAccountNo, bankAccountName, isActive, chatconeName, shiftSlots } = adminItem;
                    return (
                      <tr key={id} className={isActive === false ? 'bg-gray-50 opacity-60' : undefined}>
                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                          {index + 1}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          <div>{nickname || '-'}</div>
                          {!user && <div className="text-xs text-gray-400">no login</div>}
                          {isOwner && (
                            <span className="inline-block mt-0.5 mr-1 px-1.5 py-0.5 rounded text-xs bg-indigo-100 text-indigo-800">Owner</span>
                          )}
                          {isHelper && (
                            <span className="inline-block mt-0.5 px-1.5 py-0.5 rounded text-xs bg-teal-100 text-teal-800">Helper</span>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          <div>{chatconeName || '-'}</div>
                          {Array.isArray(shiftSlots) && shiftSlots.length > 0 && (
                            <div className="text-xs text-gray-400">{shiftSlots.length} shift{shiftSlots.length === 1 ? '' : 's'}</div>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {formatCurrency(salary)}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {workingHours ? Number(workingHours) : '-'}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {Number(fixedWhtPerMonth) ? formatCurrency(fixedWhtPerMonth) : '-'}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {studentLoan ? formatCurrency(studentLoan) : '-'}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm">
                          {isCompanyEmployee ? (
                            <span className="inline-block px-2 py-0.5 rounded text-xs font-semibold bg-blue-100 text-blue-800">
                              Company
                            </span>
                          ) : (
                            <span className="text-gray-400 text-xs">—</span>
                          )}
                          {employmentType === 'parttime' && (
                            <div>
                              <span className="inline-block mt-0.5 px-1.5 py-0.5 rounded text-xs bg-amber-100 text-amber-800">
                                Part-time · 3% WHT
                              </span>
                            </div>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          <div>{paymentFrequency === 'once' ? 'Once' : 'Twice'}</div>
                          {hasFifthRun ? (
                            <span className="inline-block mt-0.5 px-1.5 py-0.5 rounded text-xs bg-purple-100 text-purple-800">
                              + 5th run
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">no 5th run</span>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {bankAccountNo || '-'} {bankAccountName ? `(${bankAccountName})` : ''}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm">
                          <button
                            onClick={() => handleToggleActive(adminItem)}
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border transition-colors cursor-pointer ${isActive !== false ? 'bg-green-100 text-green-800 border-green-300 hover:bg-green-200' : 'bg-red-100 text-red-700 border-red-300 hover:bg-red-200'}`}
                            title={isActive !== false ? 'Click to deactivate' : 'Click to activate'}
                          >
                            {isActive !== false ? '● Active' : '○ Inactive'}
                          </button>
                        </td>
                        <td className="relative whitespace-nowrap py-4 pl-3 pr-0 text-right text-sm font-medium">
                          <div className="flex items-center space-x-3">
                            <PencilIcon 
                              onClick={() => openForm(true, id)} 
                              className='cursor-pointer flex items-center w-6 h-6 fill-green-600'
                            />
                            <TrashIcon 
                              onClick={() => openConfirm(id)} 
                              className='cursor-pointer flex items-center w-6 h-6 fill-red-600'
                            />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSetting;

